import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import type {
  BangGiaGiaSortField,
  BangGiaId,
  BangGiaTrangThai,
  CheDoApDungPhamVi,
  IBangGia,
  IBangGiaGiaHangHoa,
  IBangGiaGiaMuc,
  IBangGiaImportRow,
  IBangGiaPayload,
  IBangGiaPhamViApDung,
  IBangGiaOption,
  INguoiDungOption,
  INhomKhachHangOption,
  LoaiGiaGoc,
  LoaiNguonGia,
  ToanTuGia,
  DonViTinhGia,
  ICongThucGia,
} from '@/interfaces/bangGiaInterfaces'
import queryString from 'query-string'

const PREFIX = '/bang-gia'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

function parseNum(raw: unknown): number | null {
  if (raw === '' || raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function parseTrangThai(raw: unknown): IBangGia['trangThai'] {
  if (raw === 'AP_DUNG' || raw === 'CHUA_AP_DUNG') return raw
  return null
}

/**
 * Backend có 2 format `congThucGia` (cũ/mới) — defensive parse:
 * - Format phẳng cũ: { loaiGiaGoc, toanTu, donVi, giaTri }
 * - Format mới:       { loaiNguonGia, toanTu, donVi, giaTri, bangGiaNguonId? }
 * - `null` / thiếu → `null` (không tự invent công thức mặc định).
 */
function mapCongThucGia(raw: unknown): ICongThucGia | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  // Object rỗng hoặc không có dấu hiệu công thức thật → coi như chưa cấu hình.
  const hasSignal =
    o.toanTu != null ||
    o.giaTri != null ||
    o.donVi != null ||
    o.loaiNguonGia != null ||
    o.loaiGiaGoc != null ||
    Array.isArray(o.cong) ||
    Array.isArray(o.tru)
  if (!hasSignal) return null

  const nguonRaw = (o.loaiNguonGia ?? o.loaiGiaGoc) as unknown
  let loaiNguonGia: LoaiNguonGia = 'GIA_VON'
  if (nguonRaw === 'GIA_NHAP_CUOI') loaiNguonGia = 'GIA_NHAP_CUOI'
  else if (nguonRaw === 'GIA_BANG_GIA') loaiNguonGia = 'GIA_BANG_GIA'
  const toan = o.toanTu
  const donVi = o.donVi
  const bangGiaNguonId =
    typeof o.bangGiaNguonId === 'string' && o.bangGiaNguonId.trim()
      ? o.bangGiaNguonId
      : typeof o.bangGiaGocId === 'string' && o.bangGiaGocId.trim()
        ? o.bangGiaGocId
        : undefined
  return {
    loaiNguonGia,
    loaiGiaGoc: loaiNguonGia === 'GIA_BANG_GIA' ? undefined : (loaiNguonGia as LoaiGiaGoc),
    toanTu: (toan === 'TRU' ? 'TRU' : 'CONG') as ToanTuGia,
    giaTri: parseNum(o.giaTri) ?? 0,
    donVi: (donVi === 'PERCENT' ? 'PERCENT' : 'VND') as DonViTinhGia,
    bangGiaNguonId,
  }
}

/** Chuẩn hoá payload công thức giá trước khi gửi lên backend. */
function toCongThucBody(ct: ICongThucGia | null | undefined): Record<string, unknown> | null {
  if (!ct) return null
  return {
    loaiNguonGia: ct.loaiNguonGia ?? ct.loaiGiaGoc ?? 'GIA_VON',
    toanTu: ct.toanTu,
    giaTri: ct.giaTri,
    donVi: ct.donVi,
    bangGiaNguonId:
      (ct.loaiNguonGia ?? ct.loaiGiaGoc) === 'GIA_BANG_GIA'
        ? ct.bangGiaNguonId
        : undefined,
  }
}

function toIdArr(v: unknown): number[] {
  if (!Array.isArray(v)) return []
  return v
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n)) as number[]
}

function toStrArr(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.map((x) => String(x)).filter((s) => s.length > 0)
}

const defaultPhamViApDung: IBangGiaPhamViApDung = {
  cheDoApDungChiNhanh: 'TAT_CA',
  cheDoApDungNhomKhachHang: 'TAT_CA',
  cheDoApDungNguoiDung: 'TAT_CA',
}

/** Suy ra `cheDoApDungX` từ số lượng ID — `[]` thì `TAT_CA`, ngược lại `CU_THE`. */
function inferCheDoPhamVi(
  ids: ReadonlyArray<number | string> | undefined,
): CheDoApDungPhamVi {
  return ids && ids.length > 0 ? 'CU_THE' : 'TAT_CA'
}

function mapPhamViApDung(raw: Partial<IBangGiaPhamViApDung> | undefined): IBangGiaPhamViApDung {
  return {
    cheDoApDungChiNhanh: raw?.cheDoApDungChiNhanh ?? 'TAT_CA',
    cheDoApDungNhomKhachHang: raw?.cheDoApDungNhomKhachHang ?? 'TAT_CA',
    cheDoApDungNguoiDung: raw?.cheDoApDungNguoiDung ?? 'TAT_CA',
  }
}

export function mapBangGiaFromApi(raw: Record<string, unknown>): IBangGia {
  const dsChiNhanh = toIdArr(raw.danhSachChiNhanhIds)
  const dsNhomKH = toStrArr(raw.danhSachNhomKhachHangIds)
  const dsNguoiDung = toIdArr(raw.danhSachNguoiDungIds)

  // `phamViApDung` từ API có thể là object flat hoặc rỗng — suy ra nếu thiếu
  const apiPhamVi = raw.phamViApDung as Partial<IBangGiaPhamViApDung> | undefined
  const phamViApDungResolved: IBangGiaPhamViApDung = {
    cheDoApDungChiNhanh:
      apiPhamVi?.cheDoApDungChiNhanh ?? inferCheDoPhamVi(dsChiNhanh),
    cheDoApDungNhomKhachHang:
      apiPhamVi?.cheDoApDungNhomKhachHang ?? inferCheDoPhamVi(dsNhomKH),
    cheDoApDungNguoiDung:
      apiPhamVi?.cheDoApDungNguoiDung ?? inferCheDoPhamVi(dsNguoiDung),
  }

  return {
    id: String(raw.id ?? ''),
    ten: String(raw.ten ?? ''),
    ma: String(raw.ma ?? ''),
    laMacDinh: Boolean(raw.laMacDinh),
    ngayBatDau: String(raw.ngayBatDau ?? ''),
    ngayKetThuc: String(raw.ngayKetThuc ?? ''),
    congThucGia: mapCongThucGia(raw.congThucGia),
    choPhepThemHangNgoai: Boolean(raw.choPhepThemHangNgoai),
    canhBaoHangNgoai: Boolean(raw.canhBaoHangNgoai),
    phamViApDung: phamViApDungResolved,
    danhSachChiNhanhIds: dsChiNhanh,
    danhSachNhomKhachHangIds: dsNhomKH,
    danhSachNguoiDungIds: dsNguoiDung,
    trangThai: parseTrangThai(raw.trangThai),
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

/** Convert payload form sang body gửi backend — chỉ kèm danh sách khi `CU_THE`. */
function toCreateUpdatePayload(body: IBangGiaPayload) {
  const pv = body.phamViApDung ?? defaultPhamViApDung
  return {
    ten: body.ten.trim(),
    ngayBatDau: body.ngayBatDau.trim() || undefined,
    ngayKetThuc: body.ngayKetThuc.trim() || undefined,
    trangThai: body.trangThai,
    congThucGia: toCongThucBody(body.congThucGia),
    choPhepThemHangNgoai: body.choPhepThemHangNgoai,
    canhBaoHangNgoai: body.canhBaoHangNgoai,
    danhSachChiNhanhIds:
      pv.cheDoApDungChiNhanh === 'CU_THE' && body.danhSachChiNhanhIds.length > 0
        ? body.danhSachChiNhanhIds
        : [],
    danhSachNhomKhachHangIds:
      pv.cheDoApDungNhomKhachHang === 'CU_THE' && body.danhSachNhomKhachHangIds.length > 0
        ? body.danhSachNhomKhachHangIds
        : [],
    danhSachNguoiDungIds:
      pv.cheDoApDungNguoiDung === 'CU_THE' && body.danhSachNguoiDungIds.length > 0
        ? body.danhSachNguoiDungIds
        : [],
  }
}

export function bangGiaListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  if (q.trangThai === 'AP_DUNG' || q.trangThai === 'CHUA_AP_DUNG') out.trangThai = String(q.trangThai)
  const sort = q.sort
  if (sort === 'ma' || sort === 'ten' || sort === 'ngayTao') out.sort = sort
  const sortDir = q.sortDir
  if (sortDir === 'asc' || sortDir === 'desc') out.sortDir = sortDir
  return out
}

export function fetchBangGiaList(search: string) {
  const params = bangGiaListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-bang-gia`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapBangGiaFromApi(r as Record<string, unknown>)),
        paging,
      }
    }),
  )
}

const BANG_GIA_FILTER_TTL_MS = 60_000
let bangGiaFilterCache: { at: number; promise: Promise<IBangGia[]> } | null = null

/** 100 bảng giá cho ô lọc "Bảng giá" — dùng chung 1 request giữa các bộ lọc (cache 60s). */
export function loadBangGiaFilterOptions(): Promise<IBangGia[]> {
  if (bangGiaFilterCache && Date.now() - bangGiaFilterCache.at < BANG_GIA_FILTER_TTL_MS) {
    return bangGiaFilterCache.promise
  }
  const promise = fetchBangGiaList('?page=1&pageSize=100').then((res: unknown) => {
    const data = (res as { data?: IBangGia[] })?.data
    return Array.isArray(data) ? data : []
  })
  bangGiaFilterCache = { at: Date.now(), promise }
  promise.catch(() => {
    if (bangGiaFilterCache?.promise === promise) bangGiaFilterCache = null
  })
  return promise
}

export function fetchBangGiaDetail(id: BangGiaId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-bang-gia`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapBangGiaFromApi(raw) }
    }),
  )
}

export function taoBangGia(body: IBangGiaPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-bang-gia`, toCreateUpdatePayload(body)))
}

export function capNhatBangGia(id: BangGiaId, body: IBangGiaPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-bang-gia`, toCreateUpdatePayload(body)))
}

export function xoaBangGia(id: BangGiaId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-bang-gia`))
}

export function chuyenTrangThaiBangGia(id: BangGiaId, trangThai: BangGiaTrangThai) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-bang-gia`, { trangThai }))
}

function mapGiaHangHoaRow(raw: Record<string, unknown>): IBangGiaGiaHangHoa {
  const nhom = raw.nhomHang as Record<string, unknown> | undefined
  const nhomId = raw.nhomHangId ?? nhom?.id
  const donViRel = raw.donViTinh
  const donViTinh =
    typeof donViRel === 'string'
      ? donViRel
      : donViRel && typeof donViRel === 'object'
        ? String((donViRel as { tenDonViTinh?: unknown }).tenDonViTinh ?? '')
        : ''
  const tenNhom =
    (typeof raw.tenNhomHang === 'string' && raw.tenNhomHang) ||
    (nhom?.ten != null ? String(nhom.ten) : '') ||
    (nhom?.tenNhom != null ? String(nhom.tenNhom) : '') ||
    ''
  return {
    id: String(raw.id ?? raw.hangHoaId ?? ''),
    hangHoaId: String(raw.hangHoaId ?? raw.id ?? ''),
    maHangHoa: String(raw.maHangHoa ?? raw.ma ?? ''),
    tenHangHoa: String(raw.tenHangHoa ?? raw.ten ?? ''),
    donViTinh: donViTinh || null,
    giaVon: parseNum(raw.giaVon),
    giaNhapCuoi: parseNum(raw.giaNhapCuoi),
    giaChung: parseNum(raw.giaChung ?? raw.giaBanChung),
    giaBan: parseNum(raw.giaBan),
    tonKho: parseNum(raw.tonKho),
    nhomHangId: nhomId != null && nhomId !== '' ? String(nhomId) : null,
    nhomHangLabel: tenNhom || null,
  }
}

function buildGiaHangHoaListParams(
  q: ReturnType<typeof queryString.parse>,
): Record<string, string | number | undefined> {
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  const out: Record<string, string | number | undefined> = {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 25,
  }
  if (typeof q.search === 'string' && q.search.trim()) out.search = q.search.trim()
  if (typeof q.nhomHangId === 'string' && q.nhomHangId.trim()) out.nhomHangId = q.nhomHangId.trim()
  const ton = String(q.tonKho ?? '')
  if (ton === 'TAT_CA' || ton === 'CON_HANG' || ton === 'HET_HANG') out.tonKho = ton
  const dk = String(q.dieuKienGia ?? '')
  if (['NHO_HON', 'LON_HON', 'BANG', 'NHO_HON_HOAC_BANG', 'LON_HON_HOAC_BANG'].includes(dk)) out.dieuKienGia = dk
  const gss = String(q.giaSoSanh ?? '')
  if (gss === 'GIA_VON' || gss === 'GIA_NHAP_CUOI') out.giaSoSanh = gss
  const giaBan = Number(q.giaBan)
  if (Number.isFinite(giaBan)) out.giaBan = giaBan
  const sf = String(q.sortField ?? q.sort ?? '')
  const allowed: BangGiaGiaSortField[] = ['maHangHoa', 'tenHangHoa', 'giaVon', 'giaBan', 'tonKho']
  if (allowed.includes(sf as BangGiaGiaSortField)) {
    out.sortField = sf as BangGiaGiaSortField
    const so = String(q.sortOrder ?? q.sortDir ?? 'asc')
    if (so === 'asc' || so === 'desc') out.sortOrder = so
  }
  return out
}

export function fetchDanhSachGiaChung(search: string) {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const params = buildGiaHangHoaListParams(q)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/gia-chung/danh-sach-gia-hang-hoa`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapGiaHangHoaRow(r as Record<string, unknown>)),
        paging,
      }
    }),
  )
}

export function capNhatGiaChung(body: { hangHoaId: string; giaBan: number }) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/gia-chung/cap-nhat-gia-hang-hoa`, {
      hangHoaId: String(body.hangHoaId),
      giaBan: Number(body.giaBan),
    }),
  )
}

export function fetchDanhSachGiaHangHoaInBangGia(bangGiaId: BangGiaId, search: string) {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const params = buildGiaHangHoaListParams(q)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${bangGiaId}/danh-sach-gia-hang-hoa`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapGiaHangHoaRow(r as Record<string, unknown>)),
        paging,
      }
    }),
  )
}

export function capNhatGiaHangHoaInBangGia(bangGiaId: BangGiaId, body: { hangHoaId: string; giaBan: number }) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${bangGiaId}/cap-nhat-gia-hang-hoa`, {
      hangHoaId: String(body.hangHoaId),
      giaBan: Number(body.giaBan),
    }),
  )
}

export function capNhatGiaHangHoaHangLoat(bangGiaId: BangGiaId, danhSach: IBangGiaGiaMuc[]) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${bangGiaId}/cap-nhat-gia-hang-hoa-hang-loat`, {
      danhSachGia: danhSach.map((x) => ({
        hangHoaId: String(x.hangHoaId),
        giaBan: Math.max(0, Math.round(Number(x.giaBan) || 0)),
      })),
    }),
  )
}

export function apDungCongThucGia(bangGiaId: BangGiaId, hangHoaIds?: string[]) {
  const body =
    hangHoaIds && hangHoaIds.length > 0
      ? { hangHoaIds: hangHoaIds.map((id) => String(id)) }
      : {}
  return apiRequest(ApisServiceApp.post(`${PREFIX}/${bangGiaId}/ap-dung-cong-thuc-gia`, body))
}

export async function taiTemplateNhapGiaBlob(): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-template-nhap-gia`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function xuatFileGiaHangHoaBlob(bangGiaId: BangGiaId, search?: string): Promise<Blob> {
  const q = search ? queryString.parse(search.replace(/^\?/, '')) : {}
  const params = buildGiaHangHoaListParams(q)
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/${bangGiaId}/xuat-file-gia`, {
      ...blobResponseConfig,
      params,
    }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

/**
 * Nhập giá từ danh sách JSON (FE đã parse Excel ma trận).
 * Body = mảng dòng — giống pattern `*-nhap-danh-sach` (nhóm hàng / vị trí).
 */
export function nhapDanhSachGiaBangGia(rows: IBangGiaImportRow[]) {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/bang-gia-nhap-danh-sach`, rows, {
      timeout: 120000,
    }),
  )
}

// =====================================================================
// Options / dropdowns (dùng cho form Tạo / Sửa bảng giá)
// =====================================================================

/** Lấy danh sách bảng giá `id+ten` cho dropdown "Chọn bảng giá" (công thức). */
export async function fetchBangGiaApDungOptions(excludeId?: string): Promise<IBangGiaOption[]> {
  const res = await apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-bang-gia-ap-dung`, {
      params: excludeId ? { excludeId } : undefined,
    }).then((r: unknown) => normalizeDetailResponse<unknown>(r as { data?: unknown })),
  )
  // Backend trả `data` là array (response có thể bọc trong `data.data` tuỳ global filter)
  const arr = Array.isArray(res) ? res : Array.isArray((res as { data?: unknown[] })?.data)
    ? (res as { data: unknown[] }).data
    : []
  return (arr as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id ?? ''),
    ten: String(r.ten ?? ''),
    laMacDinh: Boolean(r.laMacDinh),
  }))
}

/** Lấy danh sách nhóm khách hàng. */
export async function fetchNhomKhachHangOptions(): Promise<INhomKhachHangOption[]> {
  const res = await apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-nhom-khach-hang-ap-dung`).then((r: unknown) =>
      normalizeDetailResponse<unknown>(r as { data?: unknown }),
    ),
  )
  const arr = Array.isArray(res) ? res : Array.isArray((res as { data?: unknown[] })?.data)
    ? (res as { data: unknown[] }).data
    : []
  return (arr as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id ?? ''),
    ma: String(r.ma ?? ''),
    tenNhom: String(r.tenNhom ?? ''),
    trangThai: String(r.trangThai ?? ''),
  }))
}

/** Lấy danh sách người dùng áp dụng. */
export async function fetchNguoiDungOptions(): Promise<INguoiDungOption[]> {
  const res = await apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-nguoi-dung-ap-dung`).then((r: unknown) =>
      normalizeDetailResponse<unknown>(r as { data?: unknown }),
    ),
  )
  const arr = Array.isArray(res) ? res : Array.isArray((res as { data?: unknown[] })?.data)
    ? (res as { data: unknown[] }).data
    : []
  return (arr as Array<Record<string, unknown>>).map((r) => ({
    id: Number(r.id ?? 0),
    username: String(r.username ?? ''),
    isActive: Boolean(r.isActive),
    role: String(r.role ?? ''),
  }))
}

/** Re-export helper backward-compat (nhiều nơi đang import từ bangGiaApi). */
export { mapPhamViApDung as _internal_mapPhamViApDung }

// Re-export downloadBlobAsFile từ utils để giữ backward-compat với các component
// đang import từ bangGiaApi. Source of truth: @/utils/downloadBlobFile.
export { downloadBlobAsFile } from '@/utils/downloadBlobFile'
